import React from 'react';
import { Switch } from 'react-router';

import type { Langcodes } from '@uncinc/react-drupal-core/types';

import { LANGUAGES } from '../../../config/constants';
import CustomRoute from '../CustomRoute';
import PrivateRoute from '../PrivateRoute';
import PublicRoute from '../PublicRoute';
import type { CustomRouteProps, RouteConfig, RouterConfig } from '../types';

const getAvailableRoutes = (routeConfig: RouteConfig, availableLanguages: Langcodes[]) => (
  availableLanguages
    .map((language) => {
      if (!(language in routeConfig)) {
        return undefined;
      }

      return routeConfig[language];
    })
    .filter((route) => route !== undefined)
);

interface RouteProps extends CustomRouteProps {
  type: RouteConfig['type'];
}

const Route: React.FC<RouteProps> = ({ type, ...restProps }) => {
  switch (type) {
    case 'public': {
      return <PublicRoute {...restProps} />;
    }

    case 'private': {
      return <PrivateRoute {...restProps} />;
    }

    case 'default':
    default: {
      return <CustomRoute {...restProps} />;
    }
  }
};

export interface RouterProps {
  config: RouterConfig<string>;
  children?: React.ReactNode;
}

const Router: React.FC<RouterProps> = ({ config, children }) => {
  const availableLanguages = Object.keys(LANGUAGES) as Langcodes[];

  return (
    <Switch>
      {Object.entries(config).map(([routeName, routeConfig]) => {
        const {
          basePath,
          type,
          exact = true,
          component: Component,
          search,
          condition,
        } = routeConfig;


        if (condition && !condition()) {
          return null;
        }

        return (
          <Route
            key={routeName}
            type={type}
            exact={exact}
            path={[basePath, ...getAvailableRoutes(routeConfig, availableLanguages)]}
            search={search}
          >
            <Component />
          </Route>
        );
      })}

      {children}
    </Switch>
  );
};

export default Router;
